import React from 'react';
import { Box } from '@mui/material';
import Toast from './Toast';
import { ToastNotification } from '../../types/errors';

interface ToastContainerProps {
  notifications: ToastNotification[];
  onRemove: (id: string) => void;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  maxToasts?: number;
}

/**
 * Container component that manages and displays multiple toast notifications
 */
export const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  onRemove,
  position = { vertical: 'top', horizontal: 'right' },
  maxToasts = 5,
}) => {
  // Limit the number of toasts displayed at once
  const visibleNotifications = notifications.slice(-maxToasts);

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'none',
        ...getPositionStyles(position),
      }}
    >
      {visibleNotifications.map((notification, index) => (
        <Box
          key={notification.id}
          sx={{
            marginBottom: index < visibleNotifications.length - 1 ? 1 : 0,
            pointerEvents: 'auto',
          }}
        >
          <Toast notification={notification} onClose={onRemove} position={position} />
        </Box>
      ))}
    </Box>
  );
};

/**
 * Get CSS styles based on toast position
 */
function getPositionStyles(position: {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}) {
  const styles: React.CSSProperties = {};

  // Vertical positioning
  if (position.vertical === 'top') {
    styles.top = 24;
  } else {
    styles.bottom = 24;
  }

  // Horizontal positioning
  if (position.horizontal === 'left') {
    styles.left = 24;
  } else if (position.horizontal === 'right') {
    styles.right = 24;
  } else {
    styles.left = '50%';
    styles.transform = 'translateX(-50%)';
  }

  return styles;
}

export default ToastContainer;
